/**
 * Tick sound generator using Web Audio API.
 * Produces a short percussive click when notes cross the playhead.
 * The default voices are synthesized in real-time (no assets); the note hit
 * can also use a recorded sample — see setSrNoteSound / hitSample.ts.
 */

import { ensureHitSample, playHitSample, type HitSampleName } from './hitSample';

let audioCtx: AudioContext | null = null;
let tickEnabled = true;
let tickVolume = 0.3;    // 0..1
let initDone = false;

/** Note-hit voice: synthesised tick, or a sample ('clap' = the Synths Riders
 *  desktop-editor hit sound). Mirrored from the editor's gameplay settings. */
export type SrNoteVoice = 'tone' | 'clap';
let noteVoice: SrNoteVoice = 'tone';
export function setSrNoteSound(v: SrNoteVoice): void {
  noteVoice = v;
  // Decode as soon as it's picked (needs a live context, which exists once the
  // user has interacted — the warm-up below also primes it).
  if (v !== 'tone' && audioCtx) void ensureHitSample(audioCtx, v as HitSampleName);
}

// Avoid retriggering the same note twice
const recentTicks = new Set<string>();

/**
 * Get or create the shared AudioContext.
 * Must be called after a user gesture (browser policy).
 */
function getContext(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

/**
 * Eagerly warm up the AudioContext on the first user interaction.
 * Browsers require a user gesture (click, keydown, wheel, etc.) to start
 * an AudioContext. This listener fires once, creates and resumes the context,
 * so that subsequent playScrollTick / playStepChangeTick calls work immediately.
 */
export function initTickAudio() {
  if (initDone) return;
  initDone = true;

  const warmUp = () => {
    try {
      const ctx = getContext();
      // First legal moment for an AudioContext → decode the sampled hit voices
      // now so the first note that needs one doesn't fall back to the tick.
      if (noteVoice !== 'tone') void ensureHitSample(ctx, noteVoice as HitSampleName);
    } catch { /* ignore */ }
    // Remove all listeners after first trigger
    window.removeEventListener('click', warmUp);
    window.removeEventListener('keydown', warmUp);
    window.removeEventListener('wheel', warmUp);
    window.removeEventListener('mousedown', warmUp);
  };

  window.addEventListener('click', warmUp, { once: false });
  window.addEventListener('keydown', warmUp, { once: false });
  window.addEventListener('wheel', warmUp, { once: false, passive: true });
  window.addEventListener('mousedown', warmUp, { once: false });
}

/**
 * Play a short tick/click sound.
 * Uses a very short oscillator burst for a clean percussive tick.
 *
 * @param pitch  — 'high' for right hand, 'low' for left hand, 'mid' for other
 * @param isNoteHit — true only for NOTE hits. The chosen hit voice (e.g. the
 *   sampled clap) applies to those alone: the metronome shares this function
 *   and must keep its own click, including the high/low downbeat accent.
 */
export function playTick(pitch: 'high' | 'low' | 'mid' = 'mid', isNoteHit = false) {
  if (!tickEnabled) return;

  try {
    const ctx = getContext();
    const now = ctx.currentTime;

    // Sampled voice (the desktop editor's clap). Falls through to the
    // synthesised tick while the sample is still decoding, so a hit is never
    // dropped — and the fallback only happens once.
    if (isNoteHit && noteVoice !== 'tone'
      && playHitSample(ctx, noteVoice as HitSampleName, now, tickVolume)) {
      return;
    }

    // Frequency based on hand
    const freq = pitch === 'high' ? 1200 : pitch === 'low' ? 800 : 1000;

    // Short oscillator burst → gain envelope → output
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    // Quick frequency sweep down for a "tick" feel
    osc.frequency.exponentialRampToValueAtTime(freq * 0.5, now + 0.03);

    gain.gain.setValueAtTime(tickVolume, now);
    // Very fast decay for percussive feel
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.05);

    // Cleanup
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  } catch {
    // Silently fail if audio context isn't ready
  }
}

/**
 * Play a softer scroll tick — a quick, quiet click for timeline scrubbing.
 * Distinct from the note tick: lower volume, shorter, softer tone.
 * Always plays regardless of tickEnabled — scrub feedback is essential UX.
 */
export function playScrollTick() {
  try {
    const ctx = getContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';  // Softer waveform than sine
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(300, now + 0.02);

    gain.gain.setValueAtTime(tickVolume * 0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.035);

    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  } catch {
    // Silently fail if audio context isn't ready
  }
}

/**
 * Play a step-change tick — a distinct higher-pitched "pop" when the user
 * increases or decreases the scroll step size via Ctrl+wheel.
 * @param direction — 'up' for finer step (higher pitch), 'down' for coarser (lower pitch)
 */
export function playStepChangeTick(direction: 'up' | 'down') {
  try {
    const ctx = getContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    // Two-tone blip: up = ascending, down = descending
    const startFreq = direction === 'up' ? 800 : 1200;
    const endFreq = direction === 'up' ? 1400 : 600;

    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, now);
    osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.06);

    gain.gain.setValueAtTime(tickVolume * 0.6, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.09);

    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  } catch {
    // Silently fail if audio context isn't ready
  }
}

/**
 * Check notes against the playhead and trigger ticks for crossings.
 * Call this every frame during playback.
 *
 * @param notes        — Array of notes for the active difficulty (sorted by time_beats)
 * @param prevBeat     — Previous frame's currentBeat
 * @param currentBeat  — This frame's currentBeat
 * @param playing      — Whether playback is active
 */
export function checkNoteCrossings(
  notes: ReadonlyArray<{ time_beats: number; hand: string; time_ms: number }>,
  prevBeat: number,
  currentBeat: number,
  playing: boolean,
) {
  if (!playing || !tickEnabled || notes.length === 0) return;

  // Only trigger on forward playback
  if (currentBeat <= prevBeat) return;

  // Find notes in the window (prevBeat, currentBeat]
  // Use binary search to find the start index efficiently
  let lo = 0;
  let hi = notes.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (notes[mid].time_beats <= prevBeat) lo = mid + 1;
    else hi = mid;
  }

  // Trigger ticks for all notes in the crossing window
  let tickCount = 0;
  const MAX_TICKS_PER_FRAME = 4; // Limit to avoid audio overload on scrub
  // Notes landing on the SAME instant (both hands on one beat — constant in
  // SR charts) get ONE hit, not one per note: identical voices summing at the
  // same instant is a straight doubling in amplitude, which reads as "the hit
  // sound is way too loud" (user report). 1ms buckets = same instant.
  const firedAtMs = new Set<number>();

  for (let i = lo; i < notes.length && tickCount < MAX_TICKS_PER_FRAME; i++) {
    const note = notes[i];
    if (note.time_beats > currentBeat) break;

    // Deduplicate: don't tick the same note twice
    const key = `${note.time_ms}`;
    if (recentTicks.has(key)) continue;

    recentTicks.add(key);

    const instant = Math.round(note.time_ms);
    if (firedAtMs.has(instant)) continue;   // simultaneous note — already sounded
    firedAtMs.add(instant);
    tickCount++;

    // Determine pitch based on hand
    const pitch = note.hand === 'right' ? 'high'
      : note.hand === 'left' ? 'low'
      : 'mid';

    playTick(pitch, true);   // a NOTE hit → honours the chosen hit voice
  }

  // Periodically clear the dedup set (every ~500ms worth of beats)
  if (recentTicks.size > 100) {
    recentTicks.clear();
  }
}

/**
 * Clear the dedup cache (call on seek or stop).
 */
export function resetTickState() {
  recentTicks.clear();
}

/**
 * Enable/disable the tick sound.
 */
export function setTickEnabled(enabled: boolean) {
  tickEnabled = enabled;
}

/**
 * Set tick volume (0..1).
 */
export function setTickVolume(vol: number) {
  tickVolume = Math.max(0, Math.min(1, vol));
}

/**
 * Whether tick sounds are currently enabled.
 */
export function isTickEnabled(): boolean {
  return tickEnabled;
}
